import React, { useEffect, useState } from "react";
import { 
  CircularProgress, 
  Box, 
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Grid
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from "moment";
import ClinicFrameConfiguracaoService from "../../../core/http/service/ClinicFrameConfiguracaoService";
import Colors from "../../../constants/Colors";

interface Props {
  empresaId: string;
}

const ClinicFrameLogs = ({ empresaId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  useEffect(() => {
    fetchLogs();
  }, [empresaId, page, rowsPerPage]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response: any = await ClinicFrameConfiguracaoService.getLogs(empresaId, page + 1, rowsPerPage);
      if (response.data && response.data.data) {
        setLogs(response.data.data);
        // Assuming the API returns total count in headers or payload. 
        // For now, let's use the length or a fixed number if not available.
        setTotalCount(response.data.totalCount || response.data.data.length); 
      }
    } catch (error) {
      console.error("Error fetching Clinic Frame logs", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Histórico de Integração</Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchLogs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Atendimento ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  Nenhum log encontrado.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{moment(log.dataEnvio).format("DD/MM/YYYY HH:mm:ss")}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.atendimentoId}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.sucesso ? "Sucesso" : "Erro"} 
                      color={log.sucesso ? "success" : "error"} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small" onClick={() => setSelectedLog(log)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Itens por página:"
        />
      </TableContainer>

      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #eee' }}>Detalhes do Log</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Payload Enviado:</Typography>
                <Box component="pre" sx={{ p: 2, bgcolor: '#f8f8f8', borderRadius: 1, overflow: 'auto', maxHeight: 200, fontSize: '0.8rem' }}>
                  {JSON.stringify(JSON.parse(selectedLog.payloadEnviado || "{}"), null, 2)}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Resposta Recebida:</Typography>
                <Box component="pre" sx={{ p: 2, bgcolor: '#f8f8f8', borderRadius: 1, overflow: 'auto', maxHeight: 200, fontSize: '0.8rem' }}>
                  {selectedLog.respostaRecebida ? (
                      selectedLog.respostaRecebida.startsWith('{') ? 
                      JSON.stringify(JSON.parse(selectedLog.respostaRecebida), null, 2) : 
                      selectedLog.respostaRecebida
                  ) : "(Vazio)"}
                </Box>
              </Grid>
              {selectedLog.errorMessage && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="error">Mensagem de Erro:</Typography>
                  <Box sx={{ p: 2, bgcolor: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 1, color: '#d32f2f', fontSize: '0.8rem' }}>
                    {selectedLog.errorMessage}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicFrameLogs;
